import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { AXIS_STROKE, AXIS_LINE, GRID_STROKE, TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, dollarTick } from "../lib/chartStyles";
import { Plus, Trash2, RefreshCw, Sparkles } from "lucide-react";
import DividendDialog from "../components/DividendDialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// Local theme-tokens used only in this file's line chart
const DIV_ITEM_STYLE = { color: "#6e8765" };
const DIV_DOT_STYLE = { fill: "#9e8193", r: 3 };

export default function Dividends() {
  const [divs, setDivs] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [openDiv, setOpenDiv] = useState(false);
  const [filter, setFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const [d, h] = await Promise.all([api.get("/dividends"), api.get("/holdings")]);
    setDivs(d.data);
    setHoldings(h.data);
  }, []);

  const autoSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/dividends/auto-sync");
      const { created, skipped, by_source } = data;
      if (created === 0) {
        toast.info(`No new payments to record. ${skipped} already on file.`);
      } else {
        toast.success(
          `Auto-recorded ${created} payment${created !== 1 ? "s" : ""}` +
            (by_source.confirmed ? ` · ${by_source.confirmed} confirmed` : "") +
            (by_source.projected ? ` · ${by_source.projected} projected` : "")
        );
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-sync failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = divs.reduce((s, d) => s + d.amount, 0);
    const year = new Date().getFullYear();
    const ytd = divs.filter(d => d.payment_date?.startsWith(`${year}`)).reduce((s, d) => s + d.amount, 0);
    const last30 = divs.filter(d => {
      const dt = new Date(d.payment_date);
      return (Date.now() - dt.getTime()) < 30 * 86400000;
    }).reduce((s, d) => s + d.amount, 0);
    return { total, ytd, last30, count: divs.length };
  }, [divs]);

  const filtered = useMemo(() => {
    if (!filter) return divs;
    return divs.filter(d => d.ticker.toLowerCase().includes(filter.toLowerCase()));
  }, [divs, filter]);

  const cumulativeData = useMemo(() => {
    const sorted = [...divs]
      .filter(d => d.payment_date && !isNaN(new Date(d.payment_date).getTime()))
      .sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));
    let running = 0;
    return sorted.map((d) => {
      running += d.amount || 0;
      return { date: d.payment_date, cumulative: Math.round(running * 100) / 100 };
    });
  }, [divs]);

  const onDelete = async (id) => {
    if (!confirm("Delete this dividend record?")) return;
    await api.delete(`/dividends/${id}`);
    toast.success("Dividend deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 flex items-center justify-between sticky top-0 bg-champagne z-10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::INCOME_LOG</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite">Dividend History</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="auto-sync-dividends-btn"
            onClick={autoSync}
            disabled={syncing || holdings.length === 0}
            className="px-4 py-2.5 bg-graphite text-champagne font-semibold text-xs tracking-wider uppercase hover:bg-graphite/90 flex items-center gap-2 disabled:opacity-50"
            title="Auto-record past payments using the catalog"
          >
            {syncing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Auto-sync from catalog
          </button>
          <button data-testid="record-dividend-btn" onClick={()=>setOpenDiv(true)} disabled={holdings.length===0} className="px-4 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light flex items-center gap-2 disabled:opacity-50">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> Manual entry
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-4 gap-px bg-stone border border-stone">
          {[
            { l: "Total Received", v: fmtMoney(stats.total), c: "text-groen-dark" },
            { l: "YTD", v: fmtMoney(stats.ytd), c: "text-groen-dark" },
            { l: "Last 30 Days", v: fmtMoney(stats.last30), c: "text-paars-dark" },
            { l: "# Payments", v: stats.count, c: "text-graphite" },
          ].map((s) => (
            <div key={s.l} className="bg-champagne p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-3">{s.l}</div>
              <div className={`text-2xl font-mono ${s.c}`}>{s.v}</div>
            </div>
          ))}
        </div>

        <div className="border border-stone bg-champagne p-6" data-testid="cumulative-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// CUMULATIVE</div>
              <h3 className="text-base font-semibold text-graphite mt-1">Lifetime Dividend Income</h3>
            </div>
            <div className="text-lg font-mono text-groen-dark">{fmtMoney(stats.total)}</div>
          </div>
          <div className="h-64">
            {cumulativeData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-graphite/45 text-sm font-mono">
                Record your first payment to see growth curve
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cumulativeData}>
                  <CartesianGrid stroke={GRID_STROKE} strokeDasharray="0" vertical={false} />
                  <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={11} tickLine={false} axisLine={AXIS_LINE} />
                  <YAxis stroke={AXIS_STROKE} fontSize={11} tickLine={false} axisLine={AXIS_LINE} tickFormatter={dollarTick} />
                  <Tooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    itemStyle={DIV_ITEM_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    formatter={(v) => [`$${v}`, "Cumulative"]}
                  />
                  <Line type="monotone" dataKey="cumulative" stroke="#88a17e" strokeWidth={2} dot={DIV_DOT_STYLE} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            value={filter}
            onChange={(e)=>setFilter(e.target.value)}
            placeholder="FILTER BY TICKER..."
            className="bg-white border border-stone px-3 py-2 text-sm font-mono text-graphite focus:border-paars focus:outline-none w-72 placeholder:text-graphite/35 uppercase tracking-widest"
            data-testid="dividend-filter"
          />
        </div>

        <div className="border border-stone bg-champagne">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone/50 border-b border-stone">
                {["Date","Ticker","Source","Amount","Per Share","Notes",""].map(h => (
                  <th key={h} className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55 ${["Amount","Per Share"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-graphite/45 font-mono text-xs">NO DIVIDEND RECORDS</td></tr>
              )}
              {filtered.map((d) => (
                <tr key={d.id} className="border-b border-stone/60 hover:bg-white">
                  <td className="px-3 py-3 font-mono text-graphite/80">{fmtDate(d.payment_date)}</td>
                  <td className="px-3 py-3 font-mono text-paars-dark">{d.ticker}</td>
                  <td className="px-3 py-3"><DividendSourceBadge source={d.source} /></td>
                  <td className="px-3 py-3 font-mono text-right text-groen-dark">{fmtMoney(d.amount)}</td>
                  <td className="px-3 py-3 font-mono text-right text-graphite/60">{d.per_share ? fmtMoney(d.per_share) : "—"}</td>
                  <td className="px-3 py-3 text-graphite/55 text-xs">{d.notes || "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={()=>onDelete(d.id)} className="p-1.5 text-graphite/60 hover:text-burgundy">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DividendDialog open={openDiv} onOpenChange={setOpenDiv} holdings={holdings} onSaved={load} />
    </div>
  );
}

function DividendSourceBadge({ source }) {
  const cfg =
    source === "auto_catalog_confirmed"
      ? { label: "Confirmed", cls: "bg-groen/25 text-groen-dark border border-groen-dark/30" }
      : source === "auto_catalog_projected"
      ? { label: "Projected", cls: "bg-paars/15 text-paars-dark border border-paars/30" }
      : source === "projected"
      ? { label: "Marked Paid", cls: "bg-paars/15 text-paars-dark border border-paars/30" }
      : { label: "Manual", cls: "bg-stone/40 text-graphite/65 border border-stone-warm" };
  return (
    <span className={`text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}
